import { Component, OnInit, ChangeDetectionStrategy, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, startWith } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { Route } from '../../sl-data/route';
import { CarrierWorker, CarrierWorkerRole } from 'src/typings/user';
import { PlanningState } from 'src/app/planning/state';

interface RouteWorker {
  id: string;
  firstName: string;
  lastName: string;
  roleId: string;
}

@Component({
  selector: 'app-edit-route',
  templateUrl: './edit-route.component.html',
  styleUrls: ['./edit-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditRouteComponent implements OnInit {
  @Input() dismissFn: () => void;
  @Input() route: Route;

  public editForm: FormGroup;
  public saving$ = new BehaviorSubject<boolean>(false);
  public workersDataSource: MatTableDataSource<RouteWorker>;
  public displayedColumns: string[] = ['name', 'role', 'actions'];
  public roles: CarrierWorkerRole[] = [];
  public workers: CarrierWorker[] = [];
  public routeWorkers$: BehaviorSubject<RouteWorker[]> = new BehaviorSubject([]);
  workerForm: FormGroup;
  filteredWorkers$: Observable<CarrierWorker[]>;

  constructor(
    private fb: FormBuilder,
    private utilities: UtilitiesService,
    private state: PlanningState
  ) {
    this.workersDataSource = new MatTableDataSource<RouteWorker>([]);
    this.workerForm = this.fb.group({
        workerId: ['', Validators.required],
        roleId: ['', Validators.required]
    });
  }

  async ngOnInit() {
    this.editForm = this.fb.group({});
    await this.load();

    // Setup the filtered workers observable
    this.filteredWorkers$ = combineLatest([
      this.workerForm.get('workerId').valueChanges.pipe(startWith('')),
      this.availableWorkers$
    ]).pipe(
      map(([searchText, workers]) => {
        const search = searchText.toLowerCase();
        return workers.filter(worker => 
          worker.firstName.toLowerCase().includes(search) || 
          worker.lastName.toLowerCase().includes(search)
        );
      })
    );
  }

  public availableWorkers$ = combineLatest([this.state.routes$, this.state.carrierWorkers$, this.routeWorkers$]).pipe(
    map(([routes, carrierWorkers, routeWorkers])=> {
      const assignedCarrierrIds = routes.filter(r=>r.id!==this.route.id).map(route => route.workers.map(routeWorker=>routeWorker.id)).flat();
      return carrierWorkers.filter(w=>(!assignedCarrierrIds.some(assignedWorkerId => assignedWorkerId===w.id)) && !routeWorkers.some(routeWorkerAssigned => routeWorkerAssigned.id===w.id))
    })
  );

  private async load() {
    
    this.workers = await firstValueFrom(this.state.carrierWorkers$);
    this.roles = await firstValueFrom(this.state.carrierWorkerRoles$);
    this.routeWorkers$.next(this.route.workers || []);
    this.workersDataSource.data = this.routeWorkers$.value;
  }

  public onRoleChange(worker: RouteWorker, event: any) {
      worker.roleId = event.value;
      this.workersDataSource.data = this.routeWorkers$.value;
  }

  public removeWorker(worker: RouteWorker) {
      this.routeWorkers$.next(this.routeWorkers$.value.filter(w => w.id !== worker.id));
      this.workersDataSource.data = this.routeWorkers$.value;
  }

  public async okayHandler() {
    try {
      this.saving$.next(true);

      await this.route.setWorkers(this.routeWorkers$.value);

      this.utilities.notifySuccess('Route workers updated successfully');
      this.dismissFn();
    } catch (error) {
      this.utilities.notifyError('Error updating route workers');
    } finally {
      this.saving$.next(false);
    }
  }

  public cancelHandler() {
    this.dismissFn();
  }

  public addWorker(){
    
    if (this.workerForm.valid) {
        const formValue = this.workerForm.value;
        const selectedWorker = this.workers.find(w => w.id === formValue.workerId);
        const selectedRoleId = formValue.roleId
        if(!selectedWorker){
          return;
        }
        this.routeWorkers$.next(this.routeWorkers$.value.filter(w => w.id !== selectedWorker.id));//remove if exists
        this.routeWorkers$.value.push({...selectedWorker, roleId: selectedRoleId});
        this.routeWorkers$.next(this.routeWorkers$.value);
        this.workersDataSource.data = this.routeWorkers$.value;
    }
    else{
        console.log(`Error: ${this.workerForm.errors}`)
    }
  }

  public displayWorkerFn = (workerId: string): string => {
    if (!workerId) return '';
    const worker = this.workers.find(w => w.id === workerId);
    return worker ? `${worker.firstName} ${worker.lastName}` : '';
  }
} 